import {
    ArrowRight,
    TrendingDown,
    Factory,
    Building2,
    Zap,
    Leaf,
    Coins,
    ArrowUpRight
} from 'lucide-react';

const CaseStudies = () => {
    const cases = [
        {
            title: 'Optimización Térmica CIP - Industria Láctea',
            type: 'Alimentaria',
            solution: 'Hybrid Heat Pump + Heat Recovery System',
            background: 'Sustitución de caldera GLP para procesos de sanitización industrial de alto flujo.',
            image: `${import.meta.env.BASE_URL}images/case_study_cheese_factory_light.png`,
            metrics: [
                { label: 'Ahorro Operativo', value: '73%', icon: <TrendingDown className="text-green-500" /> },
                { label: 'Reducción CO2', value: '355 Ton', icon: <Leaf className="text-accent" /> },
                { label: 'ROI Estimado', value: '42%', icon: <Coins className="text-yellow-500" /> },
                { label: 'Payback Period', value: '2.1 Años', icon: <Zap className="text-energy-blue" /> },
            ],
            details: [
                { label: 'Inversión', value: '$180M CLP' },
                { label: 'Ahorro Anual', value: '$87.5M CLP' },
                { label: 'Eficiencia', value: 'COP 4.2' }
            ],
            icon: <Factory className="w-8 h-8" />
        },
        {
            title: 'Suministro ACS Campamento Cordillera',
            type: 'Minería',
            solution: 'Energía Solar + BdC Híbrida',
            background: 'Suministro garantizado de 30.000L/día en condiciones climáticas extremas.',
            image: `${import.meta.env.BASE_URL}images/case_study_mining_camp_light.png`,
            metrics: [
                { label: 'Ahorro Energía', value: '80%', icon: <TrendingDown className="text-green-500" /> },
                { label: 'Costo Diesel', value: 'US$ 6,7k', icon: <ArrowRight className="text-red-400" /> },
                { label: 'Costo CIAC', value: 'US$ 1,3k', icon: <ArrowRight className="text-green-400" /> },
                { label: 'Continuidad', value: '100%', icon: <Zap className="text-energy-blue" /> },
            ],
            details: [
                { label: 'Capacidad', value: '1,570 kWh/d' },
                { label: 'SLA', value: '99.9%' },
                { label: 'Sistema', value: 'Agua-Agua' }
            ],
            icon: <Building2 className="w-8 h-8" />
        }
    ];

    return (
        <section id="casos-exito" className="py-32 bg-slate-50 relative overflow-hidden">
            <div className="section-container">
                <div className="text-center mb-24" data-aos="fade-up">
                    <div className="badge-premium mb-6">
                        <TrendingDown size={14} className="text-accent" />
                        <span>Métricas de Impacto</span>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-black text-primary-dark mb-6 tracking-tighter">
                        Resultados <span className="text-slate-400">Medibles</span>
                    </h2>
                    <p className="text-xl text-slate-500 max-w-2xl mx-auto font-light">
                        Evidencia empírica de cómo nuestra ingeniería transforma el balance financiero de nuestros clientes.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {cases.map((project, index) => (
                        <div
                            key={index}
                            className="group relative bg-white rounded-[3rem] shadow-premium border border-slate-100 overflow-hidden hover:shadow-premium-hover transition-all duration-700"
                            data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
                        >
                            {/* Visual Header */}
                            <div className="relative h-80 w-full overflow-hidden">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                                <div className="absolute top-8 left-8 right-8 flex justify-between items-start">
                                    <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white">
                                        {project.icon}
                                    </div>
                                    <div className="badge-premium bg-accent/90 border-transparent text-white">
                                        Sector {project.type}
                                    </div>
                                </div>

                                <div className="absolute bottom-8 left-8 right-8">
                                    <h3 className="text-3xl font-black text-white mb-2 tracking-tight">
                                        {project.title}
                                    </h3>
                                    <div className="text-white/60 text-sm font-medium uppercase tracking-widest">{project.solution}</div>
                                </div>
                            </div>

                            <div className="p-10">
                                <p className="text-slate-500 text-sm mb-10 leading-relaxed font-light">
                                    <strong className="text-primary-dark font-black mr-2 uppercase tracking-tighter">El Desafío:</strong>
                                    {project.background}
                                </p>

                                {/* Metrics Dashboard */}
                                <div className="grid grid-cols-2 gap-4 mb-10">
                                    {project.metrics.map((metric, mIdx) => (
                                        <div key={mIdx} className="p-6 rounded-[2rem] bg-slate-50 border border-slate-100 flex items-center gap-4 group/metric transition-all hover:bg-white hover:shadow-premium">
                                            <div className="p-3 bg-white rounded-xl shadow-sm group-hover/metric:scale-110 transition-transform">
                                                {metric.icon}
                                            </div>
                                            <div>
                                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{metric.label}</div>
                                                <div className="text-2xl font-black text-primary-dark tracking-tighter">{metric.value}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Tech Breakdown Bar */}
                                <div className="flex flex-wrap items-center justify-between gap-6 py-8 border-t border-slate-100 mt-2">
                                    <div className="flex gap-8">
                                        {project.details.map((detail, dIdx) => (
                                            <div key={dIdx}>
                                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1">{detail.label}</div>
                                                <div className="text-sm font-black text-primary-dark">{detail.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <button className="flex items-center gap-2 text-accent font-black text-xs uppercase tracking-[0.3em] group/btn">
                                        Technical Report
                                        <ArrowUpRight size={16} className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CaseStudies;
